import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import type { Feature, MultiPolygon, Polygon } from "geojson";

import { useGetSettings } from "@/api/configuration/useConfigurationAPI";
import { useGetBulkIsochroneQuery } from "@/api/isochrone/useIsochroneAPI";
import { CloseIcon } from "@/assets/icons";
import { Divider } from "@/components/ui/Divider";
import { MAX_POI } from "@/constants/dataset";
import useIsochroneCatchmentLimit from "@/hooks/spatialAnalysis/useIsochroneCatchmentLimit";
import useSpatialCatchment from "@/hooks/spatialAnalysis/useSpatialCatchment";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import {
  clearPolygonSettingsTemp,
  initialCatchmentSetting,
  savePolygonSettings,
  setCatchmentSettings,
  setCatchmentSettingsTempRoad,
  setSpatialConfig
} from "@/store/slice/spatialSlice";
import {
  CatchmentTransport,
  CatchmentType,
  ECatchmentInputBy,
  ECatchmentType,
  ERoadExclusions,
  RoadAccessMeasurement
} from "@/types/spatial";
import {
  extractCoordinatesFromDatasets,
  getRenderableCatchmentDatasets,
  rasterizedCircleArea,
  rasterizedPolygonArea
} from "@/utils/catchment";
import { sumPolygonDatasetArea } from "@/utils/dataset";
import { getInvalidContours } from "@/utils/isochrone";

import { area, circle, lineString, lineToPolygon } from "@turf/turf";
import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import CatchmentDataSection from "../catchmentDataSection";
import { countTotalRows } from "../catchmentDataSection/catchmentDataset";
import CatchmentTypeSection from "../catchmentTypeSection";

const TEST_ID_PREFIX = "catchment-settings-popup";
interface Props {
  onClose: () => void;
  onOpenDatasetTabularModal?: () => void;
}

export type CatchmentSettingsFormValues = {
  radiusData: string[];
  distances: string[];
  durations: string[];
  catchmentType: CatchmentType;
  measurementType: RoadAccessMeasurement;
  routingType: CatchmentTransport;
  roadExclusions: ERoadExclusions[];
};

const CatchmentSettingsPopup = ({
  onClose,
  onOpenDatasetTabularModal
}: Props) => {
  const dispatch = useDispatch();
  const {
    isBVTDataForCatchmentEnabled,
    isNewCatchmentEstimationGrid,
    isNewRoadExclusionEnabled
  } = useAppFeatures();
  const {
    catchmentSettings,
    catchmentSettingsTemp,
    polygonSettingsTemp,
    spatialConfig
  } = useSelector((state: RootState) => state.spatial);
  const { isDisabled } = useSpatialCatchment();
  const { isLimitReady, isRoadAccessOverLimit } = useIsochroneCatchmentLimit();

  const { data: isochroneData } = useGetBulkIsochroneQuery(
    isNewRoadExclusionEnabled,
    catchmentSettingsTemp,
    {
      // Isochrone sends one routing request per catchment input, so avoid
      // enforcing the limit until analysis settings have loaded. The fallback
      // value is only provisional.
      enabled:
        catchmentSettingsTemp.catchmentType === ECatchmentType.BY_ROAD &&
        isLimitReady &&
        !isRoadAccessOverLimit
    }
  );

  const getInvalid =
    catchmentSettingsTemp.catchmentType === ECatchmentType.BY_ROAD
      ? getInvalidContours(
          catchmentSettingsTemp.roadAccessConfig?.isochroneData ?? []
        )
      : [];

  const { data } = useGetSettings();
  const MAX_CATCHMENTS = data?.max_catchments || MAX_POI;

  const isCatchmentOverLimit =
    (!isBVTDataForCatchmentEnabled ||
      catchmentSettingsTemp.catchmentInputBy ===
        ECatchmentInputBy.BY_DATASET) &&
    countTotalRows(catchmentSettingsTemp?.datasetConfig?.datasets) >
      MAX_CATCHMENTS;

  // Over the isochrone cap the catchment stays editable but cannot be saved, so
  // the user reduces locations rather than losing the Road Access config. Only
  // applies while Road Access is the selected type — Radius has its own,
  // higher `max_catchments` limit and is unaffected.
  const isSaveBlockedByIsochroneLimit =
    catchmentSettingsTemp.catchmentType === ECatchmentType.BY_ROAD &&
    isRoadAccessOverLimit;

  const firstCatchmentDatasetId =
    catchmentSettingsTemp?.datasetConfig?.visualizationOrder?.[0];
  const firstCatchmentAggType =
    isBVTDataForCatchmentEnabled && firstCatchmentDatasetId
      ? catchmentSettingsTemp?.datasetConfig?.datasets?.[
          firstCatchmentDatasetId
        ]?.dataset?.data?.spatial_aggregation_type
      : undefined;
  // Only treat polygon catchment datasets as active in the Dataset tab.
  // Otherwise, a leftover dataset config hides CatchmentTypeSection in the
  // Location tab.
  const isPolygonDatasetCatchment =
    isBVTDataForCatchmentEnabled &&
    catchmentSettingsTemp.catchmentInputBy === ECatchmentInputBy.BY_DATASET &&
    firstCatchmentAggType === "polygon";

  // Form
  const roadAccessConfig = catchmentSettings?.roadAccessConfig;

  const formHandler = useForm<CatchmentSettingsFormValues>({
    defaultValues: {
      radiusData: !catchmentSettings?.radiusData?.length
        ? [" "]
        : catchmentSettings?.radiusData,
      catchmentType: catchmentSettings?.catchmentType,
      measurementType: roadAccessConfig?.measurementType,
      distances: roadAccessConfig?.distances,
      durations: roadAccessConfig?.durations,
      routingType: roadAccessConfig?.routingType,
      roadExclusions: roadAccessConfig?.roadExclusions
    }
  });

  const calculateAndSaveAreaSize = () => {
    if (
      catchmentSettingsTemp.catchmentType === ECatchmentType.BY_RADIUS &&
      catchmentSettingsTemp.radiusData
    ) {
      // Circle centers come from two sources, mirroring how the map renders
      // them: pinned markers (BY_LOCATION) or POIs extracted from the selected
      // datasets (BY_DATASET).
      const centers =
        catchmentSettingsTemp.catchmentInputBy === ECatchmentInputBy.BY_LOCATION
          ? (catchmentSettingsTemp.placeCoordinates ?? [])
          : extractCoordinatesFromDatasets(
              getRenderableCatchmentDatasets(
                catchmentSettingsTemp.datasetConfig?.datasets ?? {},
                catchmentSettingsTemp.datasetConfig?.visualizationOrder,
                data?.max_catchments
              )
            );

      const maxRadius = Math.max(
        0,
        ...(catchmentSettingsTemp.radiusData ?? [])
          .filter((radius) => radius.trim() !== "")
          .map((radius) => Number.parseInt(radius))
      );

      // New: only the largest radius matters (inner concentric rings are
      // contained by it), and `rasterizedCircleArea` estimates the
      // overlap-corrected, multi-marker union area on a grid — O(centres) vs a
      // `turf.union` of hundreds of circles that took tens of seconds. Legacy:
      // the area of a single max-radius circle, ignoring marker count.
      let areaSize = 0;
      if (isNewCatchmentEstimationGrid) {
        areaSize = rasterizedCircleArea(centers, maxRadius);
      } else if (maxRadius > 0) {
        areaSize = area(
          circle([0, 0], maxRadius, { steps: 64, units: "meters" })
        );
      }

      dispatch(
        setSpatialConfig({
          ...spatialConfig,
          areaSize,
          gridResolution: null,
          h3Resolution: null
        })
      );
    } else if (
      catchmentSettingsTemp.catchmentType === ECatchmentType.BY_ROAD &&
      isochroneData
    ) {
      // Turn every isochrone contour (across all markers) into a polygon.
      const isochronePolygons = isochroneData
        .flatMap((item) => item.data)
        .flatMap((collection) => collection?.features ?? [])
        .flatMap((feature): Feature<Polygon | MultiPolygon>[] => {
          const geometry = feature?.geometry;
          if (geometry?.type === "LineString") {
            return [lineToPolygon(lineString(geometry.coordinates))];
          }
          return [];
        });

      // New: rasterise the overlap-corrected, multi-marker union area — a
      // `turf.union` of many complex contours is superlinear, while
      // `rasterizedPolygonArea` is ~O(rows × vertices). Legacy: the area of the
      // single largest contour.
      let areaSize = 0;
      if (isNewCatchmentEstimationGrid) {
        areaSize = rasterizedPolygonArea(isochronePolygons);
      } else if (isochronePolygons.length) {
        areaSize = Math.max(
          ...isochronePolygons.map((polygon) => area(polygon))
        );
      }

      dispatch(
        setSpatialConfig({
          ...spatialConfig,
          areaSize,
          gridResolution: null,
          h3Resolution: null
        })
      );
    }
  };

  /**
   * Polygon-typed catchment datasets define the area themselves. Sum each
   * polygon's surface area (via `sumPolygonDatasetArea`) and stash it on
   * `spatialConfig` so downstream grid/h3 resolution logic has a non-zero
   * `areaSize` without falling through to `calculateAndSaveAreaSize`, which
   * is shaped for the BY_RADIUS / BY_ROAD inputs that polygon-mode users
   * never populate.
   */
  const applyPolygonDatasetAreaSize = () => {
    const datasets = catchmentSettingsTemp?.datasetConfig?.datasets ?? {};
    const totalArea = sumPolygonDatasetArea(datasets);
    dispatch(
      setSpatialConfig({
        ...spatialConfig,
        areaSize: totalArea,
        gridResolution: null,
        h3Resolution: null
      })
    );
  };

  // Save all input data to redux store
  const onSaveSetting = () => {
    const isPolygonTab =
      catchmentSettingsTemp.catchmentInputBy === ECatchmentInputBy.BY_POLYGON;

    if (isBVTDataForCatchmentEnabled && isPolygonDatasetCatchment) {
      applyPolygonDatasetAreaSize();
    } else if (!isBVTDataForCatchmentEnabled || !isPolygonTab) {
      calculateAndSaveAreaSize();
    }

    if (isBVTDataForCatchmentEnabled && isPolygonTab) {
      // Save Polygon tab → wipe location/dataset data so only polygons persist.
      dispatch(
        setCatchmentSettings({
          ...catchmentSettingsTemp,
          placeCoordinates: [],
          datasetConfig: initialCatchmentSetting.datasetConfig
        })
      );
      dispatch(savePolygonSettings());
    } else {
      dispatch(setCatchmentSettings(catchmentSettingsTemp));
      if (isBVTDataForCatchmentEnabled) {
        // Clear any previously drawn polygons
        dispatch(clearPolygonSettingsTemp());
        dispatch(savePolygonSettings());
      }
    }

    onClose();
  };

  useEffect(() => {
    if (isochroneData) {
      dispatch(
        setCatchmentSettingsTempRoad({
          isochroneData
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isochroneData]);

  return (
    <FormProvider {...formHandler}>
      <div
        className={twMerge(
          "z-[99] font-sans border-gainsboro bg-white rounded-lg w-[338px] max-h-[calc(100vh-54px)]",
          "flex flex-col py-[16px] absolute left-[370px] top-[30px] text-xs" // Add position: absolute
        )}
      >
        <div className="flex flex-row justify-between items-start px-4">
          <div className="flex flex-row items-center gap-2">
            <h2 className="text-txtHeading font-bold text-[20px]">Catchment</h2>
          </div>
          <button
            className="bg-transparent border-0 p-0"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <CloseIcon className="fill-dim-gray" />
          </button>
        </div>
        {/* Content */}
        <div className="flex flex-col mt-3 custom-scrollbar overflow-y-auto">
          <CatchmentDataSection
            onOpenDatasetTabularModal={onOpenDatasetTabularModal}
          />
          {(!isBVTDataForCatchmentEnabled ||
            (catchmentSettingsTemp.catchmentInputBy !==
              ECatchmentInputBy.BY_POLYGON &&
              !isPolygonDatasetCatchment)) && (
            <>
              <Divider className="bg-white-smoke w-[100%] h-[8px] min-h-[8px] my-4" />
              <CatchmentTypeSection />
            </>
          )}
        </div>
        {/* Save settings */}
        <Button
          className={twMerge(
            "h-[40px] min-h-[40px] mt-3 mx-4 border bg-teal-500 text-white text-sm font-bold",
            "disabled:border-none disabled:bg-gainsboro disabled:text-white"
          )}
          type="button"
          disabled={
            isSaveBlockedByIsochroneLimit ||
            (isBVTDataForCatchmentEnabled &&
            (catchmentSettingsTemp.catchmentInputBy ===
              ECatchmentInputBy.BY_POLYGON ||
              isCatchmentOverLimit)
              ? Object.keys(polygonSettingsTemp ?? {}).length === 0
              : isDisabled || getInvalid.length > 0)
          }
          onClick={onSaveSetting}
          data-testid={`${TEST_ID_PREFIX}-save-button`}
        >
          <span>Save</span>
        </Button>
      </div>
    </FormProvider>
  );
};

export default CatchmentSettingsPopup;
